import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Sparkle, X } from "@phosphor-icons/react";
import { streamChat } from "@/lib/aiStream";
import { useApp } from "@/context/AppContext";

const SUGGESTIONS = [
  "What is my GST liability this month?",
  "Explain my business health score",
  "Which customers owe me the most?",
  "Received ₹50,000 from Sharma Traders in HDFC via NEFT",
];

export function ChatPanel({ compact }) {
  const { company } = useApp();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setBusy(true);
    await streamChat({
      message: q,
      threadId,
      onThread: setThreadId,
      onDelta: (d) =>
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + d };
          return copy;
        }),
      onError: (err) =>
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: `⚠ ${err}` };
          return copy;
        }),
      onDone: () => setBusy(false),
    });
    setBusy(false);
  };

  return (
    <div className="flex h-full flex-col" data-testid="ai-chat-panel">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {messages.length === 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Sparkle size={16} className="text-primary" />
              <span className="font-display font-bold">FinAxis Copilot</span>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Grounded on {company?.legal_name || "your company"} books — ledgers, vouchers, GST and KPIs.
              Ask a question or describe a transaction.
            </p>
            <div className="space-y-1.5 pt-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid={`ai-suggestion-${s.slice(0, 12).replace(/\s/g, "-")}`}
                  className="w-full border border-border px-3 py-2 text-left text-xs text-muted-foreground hover:border-primary/60 hover:text-foreground"
                  style={{ transitionProperty: "color, border-color" }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className="row-in" data-testid={`ai-msg-${m.role}-${i}`}>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              {m.role === "user" ? "You" : "Copilot"}
            </p>
            <div
              className={
                m.role === "user"
                  ? "border-l-2 border-primary bg-muted/50 px-3 py-2 text-sm"
                  : "whitespace-pre-wrap px-0 py-1 text-sm leading-relaxed"
              }
            >
              {m.content || (busy ? "…" : "")}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="border-t border-border p-3">
        <Textarea
          data-testid="ai-input"
          value={input}
          rows={compact ? 2 : 3}
          placeholder="Ask FinAxis Copilot… (Enter to send)"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          className="resize-none border-border text-sm"
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Claude Sonnet 4.6</span>
          <Button size="sm" onClick={() => send()} disabled={busy} data-testid="ai-send-btn" className="h-7 gap-1 text-xs">
            {busy ? "Thinking…" : "Send"} <ArrowRight size={13} />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AiDrawer({ open, setOpen }) {
  if (!open) return null;
  return (
    <aside
      className="hidden w-[380px] shrink-0 flex-col border-l border-border bg-card lg:flex"
      data-testid="ai-drawer"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <span className="font-display text-sm font-bold">AI Copilot</span>
        <button onClick={() => setOpen(false)} data-testid="ai-drawer-close" className="text-muted-foreground hover:text-foreground">
          <X size={16} />
        </button>
      </div>
      <div className="min-h-0 flex-1">
        <ChatPanel compact />
      </div>
    </aside>
  );
}
